import { Seo } from '@frontend/shared/web';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { UserFormModal } from '../../components/admin/UserFormModal';
import { UsersTable } from '../../components/admin/UsersTable';
import { Header } from '../../components/layout/Header';
import { useAccount } from '../../contexts/AccountContext';
import { useDeleteUser } from '../../services/api/hooks/useDeleteUser';
import { useUpdateUser } from '../../services/api/hooks/useUpdateUser';
import { useUsersList } from '../../services/api/hooks/useUsersList';
import type { ExtranetUserListItem, UserStatus } from '../../types/extranet-api.types';

export const AdminRoute = () => {
  const { can } = useAccount();
  const canManage = can('admin', 'manageUsers');
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingUser, setEditingUser] = useState<ExtranetUserListItem | null>(null);

  const search = searchParams.get('search') ?? '';
  const statusFilter = (searchParams.getAll('status') as UserStatus[]) ?? [];
  const page = Math.max(1, Number(searchParams.get('page') ?? 1));
  const limit = 20;
  const isCreateOpen = canManage && searchParams.get('createUser') === '1';

  const query = useUsersList({
    page,
    limit,
    search: search || undefined,
    status: statusFilter.length > 0 ? statusFilter : undefined,
  });
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });

  const openCreate = () => updateParams(p => p.set('createUser', '1'));
  const closeCreate = () => updateParams(p => p.delete('createUser'));

  const handleToggleStatus = (user: ExtranetUserListItem) => {
    const nextStatus: UserStatus = user.status === 'active' ? 'disabled' : 'active';
    if (
      !window.confirm(
        `Confirmer ${nextStatus === 'disabled' ? 'la désactivation' : 'la réactivation'} de ${user.firstName} ${user.lastName} ?`,
      )
    )
      return;
    updateUser.mutate({ id: user.id, input: { status: nextStatus } });
  };

  const handleDelete = (user: ExtranetUserListItem) => {
    if (!window.confirm(`Supprimer définitivement ${user.firstName} ${user.lastName} ? Cette action est irréversible.`))
      return;
    deleteUser.mutate(user.id);
  };

  const refetch = () => query.refetch();

  return (
    <>
      <Seo title="Administration — Portail extranet" />
      <Header title="Administration" subtitle="Utilisateurs, rôles et périmètres" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={search}
                onChange={e =>
                  updateParams(p => {
                    if (e.target.value) p.set('search', e.target.value);
                    else p.delete('search');
                    p.set('page', '1');
                  })
                }
                placeholder="Rechercher par nom, prénom, email"
                className="h-8 px-3 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors w-72"
              />
              <div className="flex items-center gap-1.5">
                {(['active', 'disabled'] as UserStatus[]).map(s => {
                  const active = statusFilter.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() =>
                        updateParams(p => {
                          const current = p.getAll('status');
                          p.delete('status');
                          const next = active ? current.filter(x => x !== s) : [...current, s];
                          next.forEach(v => p.append('status', v));
                          p.set('page', '1');
                        })
                      }
                      className={[
                        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors duration-100',
                        active
                          ? 'bg-zinc-900 text-white border border-zinc-900'
                          : 'bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
                      ].join(' ')}
                    >
                      {s === 'active' ? 'Actif' : 'Désactivé'}
                    </button>
                  );
                })}
              </div>
            </div>
            {canManage ? (
              <button
                type="button"
                onClick={openCreate}
                className="h-8 px-3 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors duration-100"
              >
                + Nouvel utilisateur
              </button>
            ) : null}
          </div>

          <UsersTable
            items={query.data?.items ?? []}
            total={query.data?.total ?? 0}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            canManage={canManage}
            onEdit={user => setEditingUser(user)}
            onToggleStatus={handleToggleStatus}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {isCreateOpen ? (
        <UserFormModal
          mode="create"
          onClose={closeCreate}
          onSuccess={() => {
            refetch();
            closeCreate();
          }}
        />
      ) : null}

      {editingUser ? (
        <UserFormModal
          mode="edit"
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            refetch();
            setEditingUser(null);
          }}
        />
      ) : null}
    </>
  );
};
